import { mapActions, mapGetters, mapMutations } from 'vuex'
import MarkdownEditor from '@/components/MarkdownEditor/MarkdownEditor.vue'
import countries from '@/utils/_countries.json'
import languages from '@/utils/_languages.json'
import SaaSCTA from '@/components/SaaSCTA/SaaSCTA.vue'
import keywordExclusionRegex from '@/utils/keywordExclusionRegex.js'

export default {
  name: 'WriterPaid',
  data () {
    return {
      isLoading: {
        contentplans: false,
        clusterer: false,
        writer: false,
        articles: false,
        articleText: false
      },

      keywords: null,
      keywordRules: [
        value => value?.length <= 80 || 'Keyword must be 80 characters or less',
        value => value?.split(' ').length <= 10 || 'Keyword must be 10 words or less',
        value => !new RegExp(keywordExclusionRegex.regex).test(value) || 'Keyword contains invalid characters'
      ],
      domain: null,
      domainRules: [
        value => !!value || 'Domain is required',
        value => /^https:\/\/(www\.)?([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.)+[a-zA-Z]{2,}$/.test(value) || 'Domain must be a base URL like https://example.com'
      ],

      countries,
      country: 'USA',
      languages,
      language: 'en',
      serpOverlap: 2,

      interval: null,
      tableHeaders: [
        { text: 'Title', value: 'title' },
        { text: 'Slug', value: 'slug' },
        { text: 'Type', value: 'type' },
        { text: 'Created', value: 'status_updated_at' }
      ],
      clusters: null,
      newContentplanId: null,
      newArticleId: null,
      articleLoadId: 0,
      selectedArticle: null,
      selectedArticleContent: null
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    }
  },
  computed: {
    ...mapGetters([
      'activeWorkspace',
      'contentplans',
      'websocketUrl',
      'articles',
      'websocketConnected',
      'websocketInstance'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    hasActiveSubscription () {
      if (!this.activeWorkspace) return false
      const activeSubscription = this.activeWorkspace.type === 'saas' && this.activeWorkspace.subscription_active
      if (activeSubscription) this.keywordLimit = this.keywordLimitPaid
      return activeSubscription
    }
  },
  methods: {
    ...mapActions([
      'articleGet',
      'articlePut',
      'sendWebsocketMessage',
      'contentplanGetList',
      'contentplanPost',
      'articlePost'
    ]),
    ...mapMutations([
      'addProgressTracker',
      'updateProgressTracker',
      'removeProgressTracker'
    ]),
    handleWriterInitMessage (payload) {
      if (payload?.type !== 'writer-init') return
      const { contentplanId, articleId, articleTitle } = payload
      if (!contentplanId || !articleId) return
      if (this.newArticleId === articleId) return

      this.newContentplanId = contentplanId
      this.newArticleId = articleId

      this.addProgressTracker({
        contentplanId,
        clusterId: articleId,
        name: articleTitle || this.keywords,
        stepsObject: {
          Briefing: 'inProgress',
          Researching: 'pending',
          Drafting: 'pending',
          Editing: 'pending',
          Illustrating: 'pending'
        }
      })
      this.isLoading.clusterer = false
    },
    async loadData () {
      this.isLoading.contentplans = true
      try {
        await this.contentplanGetList({ workspaceId: this.workspaceId })
        const contentplans = Array.isArray(this.contentplans) ? this.contentplans : []
        const responses = await Promise.all(contentplans.map(contentplan => this.articleGet({
          contentplanId: contentplan.id,
          filters: [
            { field: 'final', op: 'is_null', value: false }
          ],
          includeExtendedMetrics: false,
          straightReturn: true
        })))
        const articles = responses.flatMap(response => response?.items || [])
        const formattedArticles = this.formatArticles(articles)
        this.$store.commit('setArticles', formattedArticles)
      } catch (e) {
        console.error(e)
      }

      this.isLoading.contentplans = false
    },
    async runWriter () {
      try {
        if (!this.keywords?.length) return
        if (!this.domain) return
        const articleTitle = this.keywords

        this.isLoading.clusterer = true

        await this.sendWebsocketMessage({
          action: 'runWriter',
          message: {
            contentplanName: 'AI Writer - ' + articleTitle,
            workspaceId: this.workspaceId,
            language: this.language,
            country: this.country,
            domain: this.domain,
            keywords: [{ keyword: articleTitle }],
            type: 'saas-writer'
          }
        })
      } catch (error) {
        console.log(error)
        this.isLoading.clusterer = false
      }
    },
    async openArticle (article) {
      if (!article?.contentplan_id || !article?.article_id) return

      try {
        const contentplanId = article.contentplan_id
        const articleId = article.article_id

        const response = await this.articleGet({
          contentplanId,
          articleId,
          includeExtendedMetrics: false,
          straightReturn: true,
          skipCommit: true
        })
        const full = response?.items?.[0]
        if (!full) return

        let sharingKey = full.sharing
        if (!sharingKey) {
          await this.articlePut({
            contentplanId,
            action: 'share',
            articleIds: [articleId],
            share: true
          })

          const responseShared = await this.articleGet({
            contentplanId,
            articleId,
            includeExtendedMetrics: false,
            straightReturn: true,
            skipCommit: true
          })
          sharingKey = responseShared?.items?.[0]?.sharing
        }
        if (!sharingKey) return

        const { href } = this.$router.resolve({
          name: 'ArticleShared',
          params: { workspaceId: this.workspaceId, contentplanId, articleId, sharingKey }
        })
        window.open(href, '_blank')
      } catch (e) {
        console.error(e)
      }
    },
    copyArticle (article) {
      if (!article) return

      const markdown = article.final
      // Add the markdown to the clipboard
      navigator.clipboard.writeText(markdown)
    },
    async closeEditor () {
      this.selectedArticle = null // Close the editor by resetting the selected article
      this.selectedArticleContent = null // Reset the selected article content
      await this.loadData() // Reload
    },
    formatArticles (articles) {
      const mapped = articles.map(article => ({
        ...article,
        title: article.title,
        slug: this.extractSlugFromUrl(article.suggested_url || article.url)
      }))
      return mapped.sort((a, b) => {
        const aDate = new Date(a.status_updated_at || a.created_at || 0).getTime()
        const bDate = new Date(b.status_updated_at || b.created_at || 0).getTime()
        return bDate - aDate
      })
    },
    extractTitleFromFinal (text) {
      if (!text) return ''
      const firstLine = text.split('\n')[0]
      const firstLineCleaned = firstLine.replace(/^#+ /, '')
      return firstLineCleaned.length > 80 ? firstLineCleaned.substring(0, 80) + '...' : firstLineCleaned
    },
    extractSlugFromUrl (url) {
      if (!url) return ''
      const segments = url.split('/').filter(segment => segment)
      return '/' + segments.pop()
    }
  },
  async mounted () {
    await this.loadData()
    document.title = 'Writer | ContentGecko'

    // Listen for progress tracker completion event
    this.$root.$on('progress-tracker-completed', () => {
      // Call loadData when all steps are completed
      this.loadData()
    })
  },
  created () {
    this.writerInitSubscription = this.$store.subscribeAction({
      after: (action) => {
        if (action.type !== 'handleWebsocketMessage' || !action.payload) return
        this.handleWriterInitMessage(action.payload)
      }
    })
  },
  beforeDestroy () {
    // Clean up event listener
    this.$root.$off('progress-tracker-completed')
    if (this.writerInitSubscription) this.writerInitSubscription()
  },
  components: {
    MarkdownEditor,
    SaaSCTA
  }
}
